import { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';

import { DesignData, Spread } from 'editor/src/store/design/types';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import useIsMounted from 'editor/src/util/useIsMounted';

import { createSpreadPreview, RequestRenderFn, SpreadPreviewDataURL } from 'editor/src/component/SpreadPreview';
import { useIsMobile } from 'editor/src/component/useDetectDeviceType';

function updateCustomFontPathCache(cache: Map<number, string>, designData: DesignData | undefined) {
  if (!designData) {
    return;
  }
  designData.spreads.forEach((spread) => {
    spread.pages.forEach((page) => {
      page.groups.media?.forEach((element) => {
        if (element.type === 'text' && element.path && element.extra?.fontFamily?.startsWith('custom-')) {
          cache.set(element.uuid, element.path);
        }
      });
    });
  });
}

function patchSpreadWithCachedPaths(spread: Spread, cache: ReadonlyMap<number, string>): Spread {
  if (cache.size === 0) {
    return spread;
  }
  let changed = false;
  const pages = spread.pages.map((page) => {
    const media = page.groups.media?.map((element) => {
      if (element.type === 'text' && !element.path && element.extra?.fontFamily?.startsWith('custom-')) {
        const cached = cache.get(element.uuid);
        if (cached) {
          changed = true;
          return { ...element, path: cached };
        }
      }
      return element;
    });
    if (!media || media === page.groups.media) {
      return page;
    }
    return { ...page, groups: { ...page.groups, media } };
  });
  return changed ? { ...spread, pages } : spread;
}

function useSpreadPreviews(
  requestRender: RequestRenderFn,
  spreadIndexes: number[],
  designKey: string,
  size: { dimension: 'height' | 'width' | 'both'; value: number },
) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const {
    spreadBackgrounds,
    spreadMasks,
    spreadForegrounds,
    gridDesigns,
    images,
    addons,
    digitizedAssets,
    designData,
  } = useSelector((state) => {
    const spreadImages = state.variants.spreadImages[designKey];
    return {
      spreadBackgrounds: spreadImages?.spreadBackgrounds ?? state.design.spreadBackgrounds,
      spreadMasks: spreadImages?.spreadMasks ?? state.design.spreadMasks,
      spreadForegrounds: spreadImages?.spreadForegrounds ?? state.design.spreadForegrounds,
      gridDesigns: state.gridDesigns.grids,
      images: state.gallery.images,
      addons: state.editorModules.addons.inUse,
      digitizedAssets: state.design.digitizedAssets,
      designData: state.design.designData,
    };
  }, shallowEqual);

  async function loadFont(fontFamily: string) {
    const loadFontOperation = await import('editor/src/store/fonts/operation/loadFontOperation');
    await dispatch(loadFontOperation.default(fontFamily));
  }

  const customFontPathCache = useRef<Map<number, string>>(new Map());
  useEffect(() => {
    updateCustomFontPathCache(customFontPathCache.current, designData);
  }, [designData]);

  const [previews, setPreviews] = useState<Array<SpreadPreviewDataURL | undefined>>(spreadIndexes.map(() => undefined));
  const isMounted = useIsMounted();

  const version = useRef(spreadIndexes.map(() => 0));
  useLayoutEffect(() => {
    setPreviews(spreadIndexes.map(() => undefined));
    version.current = spreadIndexes.map(() => 0);
  }, [spreadIndexes]);

  function renderSpreadPreview(spread: Spread, spreadIndex: number, index: number) {
    const foregroundImage = spreadForegrounds?.find((image) => image.name === spread.name);
    const backgroundImage = spreadBackgrounds?.find((image) => image.name === spread.name);
    const maskImage = spreadMasks?.find((image) => image.name === spread.name);

    version.current[index] += 1;
    const currentVersion = version.current[index];

    void createSpreadPreview(
      designKey,
      patchSpreadWithCachedPaths(spread, customFontPathCache.current),
      {
        backgroundImage,
        maskImage,
        foregroundImage,
        gridDesigns,
        images,
        addons,
      },
      spreadIndex,
      undefined,
      size,
      requestRender,
      digitizedAssets,
      loadFont,
      isMobile,
      t,
      {
        showBlanks: false,
        showBlankAreaShadowOnly: true,
        output: 'dataURL',
        strongerShadow: true,
        quality: 5,
        format: 'png',
        uniformGroundScale: true,
      },
      undefined,
      undefined,
      designData?.calendar,
    ).then((preview) => {
      if (currentVersion === version.current[index] && isMounted() && preview) {
        setPreviews((previews) => previews.map((prev, i) => (i === index ? preview : prev)));
      }
    });
  }

  useLayoutEffect(() => {
    spreadIndexes.forEach((spreadIndex, index) => {
      const spread = designData?.spreads[spreadIndex];
      if (!spread) {
        return;
      }
      renderSpreadPreview(spread, spreadIndex, index);
    });
    currentDesignData.current = designData;
  }, [images, spreadIndexes, addons, designKey, spreadBackgrounds, spreadMasks, spreadForegrounds]);

  const currentDesignData = useRef(designData);
  useLayoutEffect(() => {
    spreadIndexes.forEach((spreadIndex, index) => {
      const spread = designData?.spreads[spreadIndex];
      if (!spread || currentDesignData.current?.spreads[spreadIndex] === spread) {
        return;
      }
      renderSpreadPreview(spread, spreadIndex, index);
    });
    currentDesignData.current = designData;
  }, [designData]);

  return { previews };
}

export default useSpreadPreviews;
